import React from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";

const Hero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const is4K = useMediaQuery("(min-width: 3840px)");

  const heroContent = {
    title: "Our Strategic Alliances & Industry Footprint",
    description: "At Devangles, our strength is amplified through powerful collaborations and active participation in the industry's leading organizations.",
  };

  // Inline critical CSS for Lexend font
  const criticalCSS = `
  @font-face {
    font-family: 'Lexend';
    font-style: normal;
    font-weight: 100 900;
    font-display: optional;
    src: url('https://fonts.gstatic.com/s/lexend/v19/wlP4gS9tQPhsA8I8RZvS_a7L4xIfpQ.woff2') format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  
  /* Ensure text renders immediately with system fonts */
  .hero-title {
    font-family: 'Lexend', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  }
`;

  return (
    <>
      {/* Preload LCP Image - Use appropriate size for mobile first */}
      <link 
        rel="preload" 
        as="image" 
        href="/handshake-businessmen-480w.webp" 
        media="(max-width: 767px)"
        fetchpriority="high" 
      />
      <link 
        rel="preload" 
        as="image" 
        href="/handshake-businessmen-800w.webp" 
        media="(min-width: 768px)"
        fetchpriority="high" 
      />
      
      {/* Inline Critical Font CSS */}
      <style dangerouslySetInnerHTML={{ __html: criticalCSS }} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box
          component="section"
          sx={{
            position: "relative",
            width: "95%",
            height: {
              xs: "375px",
              sm: "450px",
              md: "736.5px",
              lg: "736.5px",
            },
            overflow: "hidden",
            backgroundColor: "#fff",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Optimized Background Image with proper srcSet */}
          <Box
            component="img"
            src="/handshake-businessmen-800w.webp" // Fallback
            srcSet={`
              /handshake-businessmen-480w.webp 480w,
              /handshake-businessmen-800w.webp 800w,
              /handshake-businessmen-1200w.webp 1200w,
              /handshake-businessmen.webp 1920w
            `}
            sizes={`
              (max-width: 480px) 480px,
              (max-width: 800px) 800px,
              (max-width: 1200px) 1200px,
              1920px
            `}
            alt="handshaking-businessmen"
            loading="eager" // Since this is LCP element
            fetchpriority="high" // Additional priority hint
            decoding="sync" // Decode synchronously for LCP
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 0,
            }}
          />

          {/* Background Overlay with Diagonal Gradient */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(135deg, rgba(0, 0, 0, 0.86) 0%, rgba(10, 10, 10, 0.48) 100%)",
              zIndex: 1,
            }}
          />

          {/* Centered Content */}
          <Box
            sx={{
              position: "absolute",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 2,
              textAlign: "center",
              padding: "20px",
            }}
          >
            {/* Title */}
            <Typography
              variant="h2"
              sx={{
                fontWeight: "bold",
                color: "#fff",
                marginBottom: "16px",
                maxWidth: {
                  xs: "100%",
                  sm: "80%",
                  md: "70%",
                  lg: is4K ? "40%" : "60%",
                },
                fontSize: {
                  xs: "40px",
                  sm: "42px",
                  md: "72px",
                  lg: "72px",
                },
              }}
            >
              {heroContent.title}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                color: "#fff",
                marginBottom: "16px",
                maxWidth: {
                  xs: "100%",
                  sm: "80%",
                  md: "70%",
                  lg: is4K ? "40%" : "70%",
                },
                fontSize: {
                  xs: "14px",
                  sm: "16px",
                  md: "30px",
                  lg: "30px",
                },
                lineHeight: 1.6,
                fontWeight: 300,
              }}
            >
              {heroContent.description}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Hero;